/**
 * Reading Activity Types
 *
 * These represent discrete events in a user's reading journey.
 * Based on the Letterboxd model for film tracking.
 */

export const ActivityType = {
  // Core reading lifecycle
  WANT_TO_READ: "want_to_read",     // Added to TBR/wishlist
  STARTED: "started",                // Began reading
  FINISHED: "finished",              // Completed book

  // Extended statuses
  DNF: "dnf",                        // Did Not Finish - stopped, won't continue
  PAUSED: "paused",                  // Temporarily stopped
  RESUMED: "resumed",                // Continued after pause

  // Social activities
  REVIEWED: "reviewed",              // Added/updated review
  RATED: "rated",                    // Added/changed rating (without review)

  // Progress tracking (optional - can be granular)
  PROGRESS_UPDATE: "progress",       // Updated page progress

  // Library management
  REMOVED: "removed",                // Removed from all shelves
  SHELVED: "shelved",                // Added to custom shelf/list
};

/**
 * Activity type display names for UI
 */
export const ActivityTypeLabels = {
  [ActivityType.WANT_TO_READ]: "Want to Read",
  [ActivityType.STARTED]: "Started Reading",
  [ActivityType.FINISHED]: "Finished",
  [ActivityType.DNF]: "Did Not Finish",
  [ActivityType.PAUSED]: "Paused",
  [ActivityType.RESUMED]: "Resumed Reading",
  [ActivityType.REVIEWED]: "Reviewed",
  [ActivityType.RATED]: "Rated",
  [ActivityType.PROGRESS_UPDATE]: "Updated Progress",
  [ActivityType.REMOVED]: "Removed",
  [ActivityType.SHELVED]: "Added to Shelf",
};

/**
 * Map old reading statuses to activity types
 */
export const StatusToActivityType = {
  "Want to Read": ActivityType.WANT_TO_READ,
  "Currently Reading": ActivityType.STARTED,
  "Read": ActivityType.FINISHED,
  "Did Not Finish": ActivityType.DNF,
  "Paused": ActivityType.PAUSED,
};

/**
 * Map activity types to current reading status
 */
export const ActivityTypeToStatus = {
  [ActivityType.WANT_TO_READ]: "Want to Read",
  [ActivityType.STARTED]: "Currently Reading",
  [ActivityType.RESUMED]: "Currently Reading",
  [ActivityType.FINISHED]: "Read",
  [ActivityType.DNF]: "Did Not Finish",
  [ActivityType.PAUSED]: "Paused",
};

/**
 * Activity types that represent an "active" reading state
 */
export const ActiveReadingActivityTypes = [
  ActivityType.STARTED,
  ActivityType.RESUMED,
  ActivityType.PROGRESS_UPDATE,
];

/**
 * Activity types that represent a "completed" state
 */
export const CompletedActivityTypes = [
  ActivityType.FINISHED,
  ActivityType.DNF,
];

/**
 * Activity types that count toward reading goals
 */
export const GoalCountingActivityTypes = [
  ActivityType.FINISHED, // All finished books count
];

/**
 * Activity types that should appear in public activity feeds
 */
export const PublicActivityTypes = [
  ActivityType.WANT_TO_READ,
  ActivityType.STARTED,
  ActivityType.FINISHED,
  ActivityType.REVIEWED,
  ActivityType.RATED,
];

/**
 * Validation: Check if activity type is valid
 */
export function isValidActivityType(type) {
  return Object.values(ActivityType).includes(type);
}

/**
 * Validation: Check if transition is allowed
 * Prevents invalid state transitions like FINISHED → STARTED (use RESUMED for rereads)
 */
export function isValidActivityTransition(fromType, toType) {
  // Allow any transition if no previous activity
  if (!fromType) return true;

  // Define invalid transitions
  const invalidTransitions = {
    [ActivityType.FINISHED]: [
      ActivityType.STARTED, // Use STARTED with isReread flag instead
    ],
    [ActivityType.DNF]: [
      ActivityType.RESUMED, // Can't resume a DNF, must use STARTED for new attempt
    ],
    [ActivityType.REMOVED]: [
      ActivityType.RESUMED,
      ActivityType.PAUSED,
      ActivityType.PROGRESS_UPDATE,
    ],
  };

  const forbidden = invalidTransitions[fromType] || [];
  return !forbidden.includes(toType);
}

/**
 * Get the current reading status from latest activity type
 */
export function getCurrentStatusFromActivity(activityType) {
  return ActivityTypeToStatus[activityType] || null;
}